import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { getAdminId } from '../../utils/getAdminId';
import { Button } from '../ui/Button';
import {
  RotateCw,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  X,
  Search,
  ImageOff,
  ChevronDown,
  ChevronUp,
  Check,
} from 'lucide-react';

interface MissingProduct {
  id: string;
  sku: string;
  name: string;
  shopify_product_id: string | null;
  shopify_variant_id: string | null;
}

interface RetryResult {
  product_id: string;
  sku: string;
  success: boolean;
  reason: string;
  match_method?: string;
}

interface RetryResponse {
  success: boolean;
  total: number;
  succeeded: number;
  failed: number;
  results: RetryResult[];
}

export const ImageRetryPanel: React.FC = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState<MissingProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [retrying, setRetrying] = useState(false);
  const [retryResults, setRetryResults] = useState<RetryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);

  const adminId = getAdminId(user);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const fetchMissingProducts = useCallback(async () => {
    if (!adminId) return;
    setLoading(true);
    try {
      const { data, error: fetchErr } = await supabase
        .from('products')
        .select('id, sku, name, shopify_product_id, shopify_variant_id')
        .eq('admin_id', adminId)
        .is('image_url', null)
        .order('sku', { ascending: true });

      if (fetchErr) throw fetchErr;
      setProducts(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!adminId) return;
      setLoading(true);
      try {
        const { data, error: fetchErr } = await supabase
          .from('products')
          .select('id, sku, name, shopify_product_id, shopify_variant_id')
          .eq('admin_id', adminId)
          .is('image_url', null)
          .order('sku', { ascending: true });

        if (cancelled) return;
        if (fetchErr) throw fetchErr;
        setProducts(data || []);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load products');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [adminId]);

  const filteredProducts = products.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return p.sku?.toLowerCase().includes(q) || p.name?.toLowerCase().includes(q);
  });

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filteredProducts.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const handleRetry = async () => {
    if (!selected.size) return;

    setRetrying(true);
    setError(null);
    setSuccess(null);
    setRetryResults(null);

    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-retry-images`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          product_ids: Array.from(selected),
        }),
        signal: AbortSignal.timeout(60000),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error((errData as Record<string, string>).error || `Server error: ${response.status}`);
      }

      const data: RetryResponse = await response.json();
      setRetryResults(data);
      setShowResults(true);

      if (data.succeeded > 0) {
        setSuccess(`Successfully synced ${data.succeeded} image${data.succeeded > 1 ? 's' : ''}`);
        fetchMissingProducts();
        setSelected(new Set());
      } else {
        setError(`No images could be synced. ${data.failed} product${data.failed > 1 ? 's' : ''} failed.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry image sync');
    } finally {
      setRetrying(false);
    }
  };

  const matchMethodLabel = (method?: string) => {
    switch (method) {
      case 'product_gid': return 'Product ID';
      case 'sku_map': return 'SKU Map';
      case 'sku_search': return 'SKU Search';
      default: return method || '';
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
          <ImageOff className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Missing Images ({products.length})
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Select products to retry image sync individually from Shopify
          </p>
        </div>
        <Button
          onClick={fetchMissingProducts}
          disabled={loading}
          variant="secondary"
          size="sm"
        >
          <RotateCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-3 w-3 text-red-600" />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
          <span className="text-sm text-green-700 dark:text-green-300">{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="h-3 w-3 text-green-600" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-cyan-600" />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-3" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            All products have images. Nothing to retry.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Filter by SKU or name..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>
            <Button
              onClick={handleRetry}
              disabled={!selected.size || retrying}
              loading={retrying}
              size="sm"
              className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
            >
              <RotateCw className="h-4 w-4 mr-1" />
              Retry Selected ({selected.size})
            </Button>
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-800/70 px-3 py-2 flex items-center gap-3 border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={toggleAll}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                  selected.size === filteredProducts.length && filteredProducts.length > 0
                    ? 'bg-cyan-600 border-cyan-600 text-white'
                    : 'border-gray-300 dark:border-gray-600 hover:border-cyan-500'
                }`}
              >
                {selected.size === filteredProducts.length && filteredProducts.length > 0 && (
                  <Check className="h-3 w-3" />
                )}
              </button>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider flex-1">
                {selected.size > 0 ? `${selected.size} selected` : `${filteredProducts.length} products`}
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {filteredProducts.filter(p => p.shopify_product_id).length} linked to Shopify
              </span>
            </div>

            <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
              {filteredProducts.map(p => (
                <button
                  key={p.id}
                  onClick={() => toggleSelect(p.id)}
                  className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${
                    selected.has(p.id)
                      ? 'bg-cyan-50 dark:bg-cyan-900/10'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800/30'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      selected.has(p.id)
                        ? 'bg-cyan-600 border-cyan-600 text-white'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {selected.has(p.id) && <Check className="h-3 w-3" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-gray-900 dark:text-gray-100">{p.sku}</span>
                      {p.shopify_product_id && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                          Linked
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{p.name}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {retryResults && (
            <div className="mt-4">
              <button
                onClick={() => setShowResults(!showResults)}
                className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 mb-2"
              >
                {showResults ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                Retry Results ({retryResults.succeeded} succeeded, {retryResults.failed} failed)
              </button>

              {showResults && (
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {retryResults.results.map((r, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                        r.success
                          ? 'bg-green-50 dark:bg-green-900/10'
                          : 'bg-red-50 dark:bg-red-900/10'
                      }`}
                    >
                      {r.success ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                      )}
                      <span className="font-mono text-xs text-gray-900 dark:text-gray-100">{r.sku}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex-1 truncate">
                        {r.reason}
                      </span>
                      {r.match_method && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded flex-shrink-0">
                          {matchMethodLabel(r.match_method)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
